import { Ban } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  DURATION_LABELS, callerLabel, canUnblock, durationsFor, outlasts, untilPhrase,
  type BlockDuration, type BlockTarget, type BlockedCaller,
} from "@/lib/blocked-callers";
import { useBlockCaller, useBlockOn, useUnblockCaller } from "@/lib/blocked-callers-api";
import { isOutsideNumber, toE164 } from "@/lib/phone-number";
import { cn, formatPhoneNumber } from "@/lib/utils";

/**
 * Block a caller: how long, and why. `target` null asks for the caller as well (Settings). The
 * lengths offered are the ones the server takes from this person: a rep up to a day, a supervisor
 * for good too, and hidden callers never for good. A block already in force is said, and one
 * that lasts at least as long as the choice is left alone; a supervisor can end it here, after
 * the same question Settings asks.
 *
 * A typed number is checked here as the server would check it (`isOutsideNumber`) and sent in
 * E.164, the spelling the server keeps it in. What the toast says comes from the block the server
 * saved, which is not always the one chosen: a caller already blocked for longer stays so.
 */
export function BlockCallerDialog({
  open, onOpenChange, target, onBlocked,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: BlockTarget | null;
  /** After the block is saved: the ringing call it came from is declined, say. */
  onBlocked?: (block: BlockedCaller) => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const save = useBlockCaller();
  const ending = useEndBlock();
  const [kind, setKind] = React.useState<"number" | "hidden">("number");
  const [typed, setTyped] = React.useState("");
  const [duration, setDuration] = React.useState<BlockDuration>("1h");
  const [note, setNote] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [unblocking, setUnblocking] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setKind("number");
    setTyped("");
    setDuration("1h");
    setNote("");
    setError(null);
    setUnblocking(false);
  }, [open]);

  const hidden = target ? target.hidden : kind === "hidden";
  const number = target ? target.number : hidden ? null : typed.trim();
  const choices = durationsFor(user?.role, hidden);
  const chosen = choices.includes(duration) ? duration : "1h";
  // A typed number is looked up once it is a whole one, not at every keystroke on the way there.
  const existing = useBlockOn(open && (hidden || isOutsideNumber(number)) ? { number, hidden } : null);
  const already = existing !== undefined && outlasts(existing, chosen);
  const who = hidden
    ? "hidden callers"
    : target?.name || (number ? formatPhoneNumber(number) : "a caller");

  const submit = () => {
    setError(null);
    const e164 = hidden ? null : toE164(number);
    if (!hidden && e164 === null) {
      setError(
        target
          ? "This is not a phone number a caller can call from, so it cannot be blocked."
          : number
            ? "Type a full phone number, like (310) 555-0142, or one starting with + and its country code."
            : "Type the number to block.",
      );
      return;
    }
    save.mutate(
      {
        ...(e164 === null ? { hidden: true } : { number: e164 }),
        duration: chosen,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(target?.leadId ? { lead_id: target.leadId } : {}),
        ...(target?.callId ? { call_id: target.callId } : {}),
      },
      {
        onSuccess: (block) => {
          toast.success(
            block.expires_at === null ? "Blocked for good" : `Blocked ${untilPhrase(block)}`,
            callerLabel(block),
          );
          onBlocked?.(block);
          onOpenChange(false);
        },
        onError: (e) => setError(e instanceof ApiError ? e.message : "Could not block. Please try again."),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{target ? `Block ${who}` : "Block a caller"}</DialogTitle>
          <DialogDescription>
            {hidden
              ? "Every caller who hides their number hears “That person does not accept anonymous calls” and is hung up on. No phone rings."
              : "The number hears a busy tone on every number we own. No phone rings, and nothing reaches the missed calls."}
          </DialogDescription>
        </DialogHeader>
        <DialogForm
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <DialogBody className="space-y-4">
            {!target && (
              <Field label="Who" htmlFor="block-kind">
                <div id="block-kind" role="radiogroup" aria-label="Who" className="grid grid-cols-2 gap-2">
                  {(["number", "hidden"] as const).map((k) => (
                    <Choice key={k} on={kind === k} onPick={() => setKind(k)}>
                      {k === "number" ? "A number" : "Hidden callers"}
                    </Choice>
                  ))}
                </div>
              </Field>
            )}
            {!target && kind === "number" && (
              <Field label="Number" htmlFor="block-number" required>
                <Input
                  id="block-number"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder="(310) 555-0142"
                  autoComplete="off"
                  inputMode="tel"
                />
              </Field>
            )}
            <Field
              label="For how long"
              htmlFor="block-duration"
              hint={
                hidden
                  ? "Hidden callers are never blocked for good: a real lead may call from a hidden number."
                  : choices.includes("forever")
                    ? undefined
                    : "A supervisor can block a number for good."
              }
            >
              <div
                id="block-duration"
                role="radiogroup"
                aria-label="For how long"
                className={cn("grid gap-2", choices.length === 4 ? "grid-cols-4" : "grid-cols-3")}
              >
                {choices.map((d) => (
                  <Choice key={d} on={chosen === d} onPick={() => setDuration(d)}>
                    {DURATION_LABELS[d]}
                  </Choice>
                ))}
              </div>
            </Field>
            <Field label="Why" htmlFor="block-note" hint="Optional. The team sees it on the list.">
              <Textarea
                id="block-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={300}
                rows={2}
                className="font-sans text-sm"
                placeholder="Calls over and over, already told no"
              />
            </Field>
            {existing && (
              <Callout tone="info">
                Already blocked {untilPhrase(existing)}
                {existing.blocked_by ? ` by ${existing.blocked_by}` : ""}.
                {already ? " That lasts at least as long as this." : " This makes it longer."}
              </Callout>
            )}
            {error && <Callout tone="danger">{error}</Callout>}
          </DialogBody>
          <DialogFooter>
            {existing && canUnblock(user?.role) && (
              <Button
                type="button"
                variant="ghost"
                className="mr-auto"
                disabled={save.isPending}
                onClick={() => setUnblocking(true)}
              >
                Unblock
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" loading={save.isPending} disabled={already || ending.pending}>
              <Ban />
              Block
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
      <UnblockConfirm
        block={unblocking ? (existing ?? null) : null}
        onOpenChange={(next) => !next && setUnblocking(false)}
        loading={ending.pending}
        onConfirm={async (block) => {
          if (!(await ending.end(block))) return;
          setUnblocking(false);
          onOpenChange(false);
        }}
      />
    </Dialog>
  );
}

/** End a block early and say so: true once it has ended. */
export function useEndBlock(): { end: (block: BlockedCaller) => Promise<boolean>; pending: boolean } {
  const toast = useToast();
  const unblock = useUnblockCaller();
  const end = async (block: BlockedCaller) => {
    try {
      await unblock.mutateAsync(block.id);
      toast.success("Unblocked", callerLabel(block));
      return true;
    } catch (e) {
      toast.error("Could not unblock", e instanceof ApiError ? e.message : "Please try again.");
      return false;
    }
  };
  return { end, pending: unblock.isPending };
}

/**
 * "Unblock this caller?", asked before a block ends early wherever that is done (Settings' list,
 * the block dialog), so it is the same question in the same words. Open while `block` is set.
 */
export function UnblockConfirm({
  block, onOpenChange, onConfirm, loading,
}: {
  block: BlockedCaller | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (block: BlockedCaller) => void | Promise<void>;
  loading: boolean;
}) {
  return (
    <ConfirmDialog
      open={block !== null}
      onOpenChange={onOpenChange}
      title="Unblock this caller?"
      description={
        <>
          <span className="font-medium">{block ? callerLabel(block) : ""}</span> rings the team
          again from their next call.
        </>
      }
      confirmLabel="Unblock"
      loading={loading}
      onConfirm={() => {
        if (block) void onConfirm(block);
      }}
    />
  );
}

function Choice({ on, onPick, children }: { on: boolean; onPick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onPick}
      className={cn(
        "rounded-md border px-2 py-1.5 text-center text-sm",
        on
          ? "border-accent-8 bg-accent-3 font-medium text-accent-11"
          : "border-border bg-surface text-text hover:bg-surface-hover",
      )}
    >
      {children}
    </button>
  );
}
